import {
  Box, CardHeader, CardContent, Grid, TextField, Button, IconButton, Paper, Stack, FormControlLabel, Checkbox, Typography, Chip, FormHelperText
} from "@mui/material";
import { Add, Delete, Link as LinkIcon, Public } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import errorHandler from "../../../utils/errorHandler";
import CardLayout from "../../../layouts/CardLayout";
import { toast } from "react-toastify";
import LoadingButton from "../../../components/Buttons/LoadingButton";
import { ResponsibleGamblingApi } from "../../../services/AllServices";
import EdiorJodit from "../../../components/editor/editorJodit";

const ResponsibleGambling = () => {
    const { id } = useParams();
    const pageType = id ? "Update" : "Update";

    const [cmsData, setCmsData] = useState({});
    const [loading, setLoading] = useState(false);
    const [contentLinks, setContentLinks] = useState([]);

  useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const { data } = await ResponsibleGamblingApi.getResponsibleGamblingById();
                setCmsData(data.data);
                // Ensure each link has isExternal field, defaulting to false if not present
                const linksWithExternal = (data.data?.contentLinks || []).map(link => ({
                    ...link,
                    isExternal: link.isExternal || false
                }));
                setContentLinks(linksWithExternal);
            } catch (error) {
                errorHandler(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Validation Schema
    const editorContentValidation = (cmsData) => {
        if (cmsData === undefined) {
            return false;
        }
        const plainText = cmsData.replace(/<[^>]+>/g, "").trim();
        if (!plainText) {
            return false;
        }
        return true;
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        description: Yup.string().test(
            "required-description",
            "Description is required",
            (value) => editorContentValidation(value)
        ),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: cmsData?.title || "",
            description: cmsData?.description || "",
        },
        validationSchema,
        onSubmit: async (values, helpers) => {
            await handleSubmit(values, helpers);
        },
    });

    const handleAddLink = () => {
        const newLink = { label: "", url: "", isExternal: false };
        const updated = [...contentLinks, newLink];
        setContentLinks(updated);
    };

    const handleRemoveLink = (index) => {
        const updated = contentLinks.filter((_, i) => i !== index);
        setContentLinks(updated);
    };

    const handleLinkChange = (index, field, value) => {
        const updated = [...contentLinks];
        updated[index][field] = value;
        setContentLinks(updated);
    };

    async function handleSubmit(values, { setSubmitting }) {
        setSubmitting(true);
        try {
            const payload = {
                title: values.title,
                description: values.description,
                contentLinks: contentLinks
            };
            
            // Send the _id from the API response as 'id' in the payload
            if (cmsData._id) payload.id = cmsData._id;

            const resp = await ResponsibleGamblingApi.update(payload);
            if (resp.data.status) {
                toast.success(resp.data.message);
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            errorHandler(error);
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <Box>
                <CardLayout>
                    <CardHeader title="Loading..." />
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                Loading responsible gambling data...
                            </Typography>
                        </Box>
                    </CardContent>
                </CardLayout>
            </Box>
        );
    }

    return (
        <Box>
            <CardLayout>
                <CardHeader title={`${pageType} Responsible Gambling`} />
                <CardContent>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                            <Grid container spacing={4}>
                                {/* Basic Information Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                                            Basic Information
                                        </Typography>
                                        
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="title"
                                                    label="Title"
                                                    value={formik.values.title}
                                                    onChange={formik.handleChange}
                                                    required
                                                    error={Boolean(formik.touched.title && formik.errors.title)}
                                                    helperText={formik.touched.title && formik.errors.title}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                                    Description
                                                </Typography>
                                                <EdiorJodit
                                                    formik={formik}
                                                    fieldName="description"
                                                    onChange={(val) => formik.setFieldValue("description", val)}
                                                    onBlur={() => formik.setFieldTouched("description", true)}
                                                    data={formik.values.description}
                                                    placeholder="Enter description"
                                                />
                                                {formik.touched.description && formik.errors.description && (
                                                    <FormHelperText error sx={{ display: 'block', mt: 1 }}>
                                                        {formik.errors.description}
                                                    </FormHelperText>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Content Links Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                Content Links
                                            </Typography>
                                        </Box>
                                        
                                        {/* Add Link Button - Better positioned */}
                                        <Box sx={{ mb: 3, textAlign: 'right' }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<Add />}
                                                onClick={handleAddLink}
                                                sx={{ 
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 3
                                                }}
                                            >
                                                Add New Link
                                            </Button>
                                        </Box>
                                        {contentLinks.map((link, index) => (
                                            <Paper 
                                                key={index} 
                                                elevation={2} 
                                                sx={{ 
                                                    p: 3, 
                                                    mb: 3, 
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                        transition: 'box-shadow 0.3s ease-in-out'
                                                    }
                                                }}
                                            >
                                                <Stack spacing={3}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                Link {index + 1}
                                                            </Typography>
                                                            {link.isExternal && (
                                                                <Chip 
                                                                    label="External" 
                                                                    size="small" 
                                                                    color="primary" 
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => handleRemoveLink(index)}
                                                            sx={{ 
                                                                '&:hover': { 
                                                                    backgroundColor: 'error.light',
                                                                    color: 'white'
                                                                }
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>
                                                    
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Label"
                                                                value={link.label}
                                                                onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="URL"
                                                                value={link.url}
                                                                onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                                                                variant="outlined"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={link.isExternal || false}
                                                                        onChange={(e) => handleLinkChange(index, 'isExternal', e.target.checked)}
                                                                        sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Public sx={{ fontSize: 20, color: 'primary.main' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                            External Link
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                            </Paper>
                                        ))}
                                        
                                        {contentLinks.length === 0 && (
                                            <Box sx={{ 
                                                textAlign: 'center', 
                                                py: 6, 
                                                color: 'text.secondary',
                                                border: '2px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                backgroundColor: 'grey.50'
                                            }}>
                                                <LinkIcon sx={{ 
                                                    fontSize: 48, 
                                                    color: 'text.secondary', 
                                                    mb: 2,
                                                    opacity: 0.6
                                                }} />
                                                <Typography variant="body1" sx={{ mb: 1 }}>
                                                    No content links added yet
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Click "Add New Link" to get started
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Add Link Button at Bottom */}
                                        {contentLinks.length > 0 && (
                                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Add />}
                                                    onClick={handleAddLink}
                                                    sx={{ 
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 3
                                                    }}
                                                >
                                                    Add Another Link
                                                </Button>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Submit Button Section */}
                            <Box sx={{ 
                                mt: 4, 
                                pt: 3, 
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}>
                                <LoadingButton 
                                    text={pageType} 
                                    loading={formik.isSubmitting}
                                />
                            </Box>
                        </Form>
                    </FormikProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
};

export default ResponsibleGambling;